/**
 * Fill a gap in the condition ladder without pretending it is a sale.
 *
 * Across a recent scanning session, of 61 priced rows: near mint was present on
 * 56 and lightly played on 55, but moderately played on only 28, heavily played
 * on 22 and damaged on 31. So the middle and bottom of the ladder are blank more
 * often than not, and a blank is useless to someone holding a moderately played
 * card - it is the one number they came for.
 *
 * An estimate is not a price. Everything here is labelled as an estimate at the
 * point it is shown, never counts toward a collection total, and never gets
 * written to the cache. It exists to answer "roughly what is this worth" when
 * nobody has sold one recently.
 */

/** Best to worst. The order is the whole basis of the interpolation. */
export const LADDER = ['nm', 'lp', 'mp', 'hp', 'dmg'];

/**
 * Where each condition sits relative to near mint.
 *
 * The same figures the pricing service already falls back on, so an estimate
 * here and an estimate there agree. They are a shape, not a claim: two known
 * neighbours always beat them, and they are only used alone when there is
 * nothing to interpolate between.
 */
export const CONDITION_SCALE = {
  nm: 1.0,
  lp: 0.8,
  mp: 0.6,
  hp: 0.4,
  dmg: 0.25,
};

/**
 * @param {Object} known  { nm: 12.5, lp: 9.0, ... } - only conditions with real sales
 * @returns {Object} { mp: { price, from: ['lp','hp'], estimated: true }, ... }
 *
 * Returns entries only for conditions that are missing and can be estimated.
 */
export function estimateMissing(known = {}) {
  const have = LADDER.filter((c) => Number(known[c]) > 0);
  if (!have.length) return {};

  const out = {};

  for (const target of LADDER) {
    if (Number(known[target]) > 0) continue;

    const idx = LADDER.indexOf(target);
    // Nearest real price on each side of the gap. Several missing in a row is
    // the normal case, not the exception, so this walks outward rather than
    // looking only at the immediate neighbours.
    const better = have.filter((c) => LADDER.indexOf(c) < idx).pop() || null;
    const worse = have.find((c) => LADDER.indexOf(c) > idx) || null;

    const scale = CONDITION_SCALE[target];
    let price = null;
    let from = [];

    if (better && worse) {
      // Interpolate on the condition scale rather than on position, so a gap of
      // two steps is weighted the way the market actually weights it.
      const hi = known[better];
      const lo = known[worse];
      const sHi = CONDITION_SCALE[better];
      const sLo = CONDITION_SCALE[worse];
      const span = sHi - sLo;
      const t = span === 0 ? 0.5 : (scale - sLo) / span;
      price = lo + (hi - lo) * t;
      from = [better, worse];
    } else if (better) {
      // Below everything known: scale down from the worst real price we have.
      price = known[better] * (scale / CONDITION_SCALE[better]);
      from = [better];
    } else if (worse) {
      // Above everything known, which happens when only a damaged copy sold.
      price = known[worse] * (scale / CONDITION_SCALE[worse]);
      from = [worse];
    }

    if (price == null || !Number.isFinite(price) || price <= 0) continue;

    out[target] = {
      price: Math.round(price * 100) / 100,
      from,
      estimated: true,
      // Interpolating between two real prices is a far better guess than
      // scaling off one, and the screen says which it did.
      basis: from.length === 2 ? 'between' : 'scaled',
    };
  }

  return out;
}

/** "Estimated between LP and HP" / "Estimated from NM". */
export function describeEstimate(entry, labels = {}) {
  if (!entry?.from?.length) return 'Estimated';
  const name = (c) => labels[c] || String(c).toUpperCase();
  return entry.from.length === 2
    ? `No sales. Estimated between ${name(entry.from[0])} and ${name(entry.from[1])}`
    : `No sales. Estimated from ${name(entry.from[0])}`;
}

export default { estimateMissing, describeEstimate, LADDER, CONDITION_SCALE };
